<template>
  <div class="h-app">
    <!-- Navigation -->
    <nav
      class="h-nav"
      :class="{ 'is-shrunk': isScrolled }"
    >
      <div class="h-container h-nav-inner">
        <div class="h-brand">
          <img
            :src="appIcon"
            :alt="content.images.logoAlt"
            class="h-logo"
          >
          <span class="h-title">{{ content.brand }}</span>
        </div>
        <div class="h-nav-links desktop-only">
          <a href="#tools">{{ content.nav.skills }}</a>
          <a href="#tests">{{ content.nav.tests }}</a>
          <a href="#guidance">{{ content.nav.guidance }}</a>
          <a href="#features">{{ content.nav.features }}</a>
          <a href="#how-it-works">{{ content.nav.experience }}</a>
          <a href="#reviews">{{ content.nav.community }}</a>
          <a :href="allNewsUrl">{{ content.nav.news }}</a>
        </div>
        <div class="h-nav-actions">
          <label class="h-language-picker">
            <span class="sr-only">{{ content.languageLabel }}</span>
            <select
              :value="locale"
              :aria-label="content.languageLabel"
              @change="changeLocale"
            >
              <option
                v-for="option in localeOptions"
                :key="option.code"
                :value="option.code"
              >
                {{ option.name }}
              </option>
            </select>
          </label>
          <a
            :href="downloadLinks.ios"
            class="h-cta-small"
          >{{ content.nav.download }}</a>
        </div>
      </div>
    </nav>

    <main>
      <!-- Hero Section -->
      <section class="h-hero">
        <div class="h-container h-hero-grid">
          <div class="h-hero-text">
            <h1 class="h-hero-title">
              {{ content.hero.titleLead }}<br>
              <span class="h-accent">{{ content.hero.titleAccent }}</span>
            </h1>
            <p class="h-hero-subtitle">
              {{ content.hero.subtitle }}
            </p>
            <div class="h-cta-group">
              <a
                :href="downloadLinks.ios"
                target="_blank"
                rel="noopener noreferrer"
                class="h-badge apple"
              >
                <img
                  :src="appleLogo"
                  :alt="content.stores.appleName"
                >
                <div class="h-badge-text">
                  <span class="h-small">{{ content.stores.applePrefix }}</span>
                  <span class="h-large">{{ content.stores.appleName }}</span>
                </div>
              </a>
              <a
                :href="downloadLinks.android"
                target="_blank"
                rel="noopener noreferrer"
                class="h-badge google"
              >
                <img
                  :src="googlePlayLogo"
                  :alt="content.stores.googleName"
                >
                <div class="h-badge-text">
                  <span class="h-small">{{ content.stores.googlePrefix }}</span>
                  <span class="h-large">{{ content.stores.googleName }}</span>
                </div>
              </a>
            </div>
          </div>

          <div class="h-hero-visual">
            <div class="h-screen-matrix">
              <div
                v-for="(img, i) in screens"
                :key="i"
                class="h-screen-item"
                :style="{ '--i': i }"
                @click="openLightbox(img)"
              >
                <img
                  :src="img"
                  :alt="`${content.images.screenAlt} ${i + 1}`"
                >
              </div>
            </div>
            <div class="h-hero-glow" />
          </div>
        </div>
      </section>

      <!-- Skills Section -->
      <section
        id="tools"
        class="h-tools reveal-on-scroll"
        aria-labelledby="tools-title"
      >
        <div class="h-container">
          <div class="h-tools-shell">
            <div class="h-tools-layout">
              <div class="h-tools-intro">
                <span class="h-eyebrow">{{ content.tools.eyebrow }}</span>
                <h2
                  id="tools-title"
                  class="h-section-title"
                >
                  {{ content.tools.title }}
                </h2>
                <p class="h-tools-copy">
                  {{ content.tools.description }}
                </p>
              </div>
              <div class="h-tools-grid">
                <a
                  v-for="tool in content.tools.items"
                  :key="tool.title"
                  :href="tool.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="h-tool-card"
                  :style="{ '--tool-glow': tool.glow, '--tool-accent': tool.accent }"
                  :aria-label="`${content.tools.action}: ${tool.title}`"
                >
                  <div class="h-tool-head">
                    <span class="h-tool-emoji">{{ tool.emoji }}</span>
                    <div class="h-tool-heading">
                      <span class="h-tool-tag">{{ tool.tag }}</span>
                      <h3>{{ tool.title }}</h3>
                    </div>
                  </div>
                  <p>{{ tool.description }}</p>
                  <span class="h-tool-link">{{ content.tools.action }}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Self-Discovery Tests -->
      <section
        id="tests"
        class="h-assessments reveal-on-scroll"
        aria-labelledby="tests-title"
      >
        <div class="h-container">
          <div class="h-section-header h-section-header--compact">
            <span class="h-eyebrow">{{ content.assessments.eyebrow }}</span>
            <h2
              id="tests-title"
              class="h-section-title"
            >
              {{ content.assessments.title }}
            </h2>
            <p class="h-section-copy">
              {{ content.assessments.description }}
            </p>
          </div>
          <div class="h-assessment-grid">
            <a
              v-for="assessment in content.assessments.items"
              :key="assessment.title"
              :href="assessment.url"
              target="_blank"
              rel="noopener noreferrer"
              class="h-assessment-card"
              :style="{ '--assessment-color': assessment.color }"
            >
              <div class="h-assessment-topline">
                <span
                  class="h-assessment-icon"
                  aria-hidden="true"
                >{{ assessment.icon }}</span>
                <span class="h-assessment-meta">{{ assessment.meta }}</span>
              </div>
              <span class="h-assessment-eyebrow">{{ assessment.eyebrow }}</span>
              <h3>{{ assessment.title }}</h3>
              <p>{{ assessment.description }}</p>
              <span class="h-assessment-link">{{ content.assessments.action }} <span aria-hidden="true">→</span></span>
            </a>
          </div>
          <p class="h-assessment-disclaimer">
            {{ content.assessments.disclaimer }}
          </p>
        </div>
      </section>

      <!-- AI Mystical Guidance -->
      <section
        id="guidance"
        class="h-guidance reveal-on-scroll"
        aria-labelledby="guidance-title"
      >
        <div class="h-container h-guidance-layout">
          <div class="h-guidance-intro">
            <span class="h-eyebrow">{{ content.guidance.eyebrow }}</span>
            <h2
              id="guidance-title"
              class="h-section-title"
            >
              {{ content.guidance.title }}
            </h2>
            <p>
              {{ content.guidance.description }}
            </p>
          </div>
          <div class="h-guidance-cards">
            <a
              v-for="guide in content.guidance.items"
              :key="guide.title"
              :href="guide.url"
              target="_blank"
              rel="noopener noreferrer"
              class="h-guidance-card"
            >
              <span
                class="h-guidance-icon"
                aria-hidden="true"
              >{{ guide.icon }}</span>
              <div>
                <h3>{{ guide.title }}</h3>
                <p>{{ guide.description }}</p>
                <small>{{ guide.note }}</small>
              </div>
            </a>
          </div>
        </div>
      </section>

      <!-- Features Section -->
      <section
        id="features"
        class="h-features reveal-on-scroll"
      >
        <div class="h-container">
          <div class="h-section-header">
            <span class="h-eyebrow">{{ content.features.eyebrow }}</span>
            <h2 class="h-section-title">
              {{ content.features.title }}
            </h2>
          </div>
          <div class="h-features-grid">
            <div
              v-for="(f, i) in content.features.items"
              :key="i"
              class="h-feature-card"
            >
              <div
                class="h-feature-icon"
                :style="{ '--bg': f.border }"
              >
                {{ f.emoji }}
              </div>
              <h3>{{ f.title }}</h3>
              <p>{{ f.description }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- How it Works -->
      <section
        id="how-it-works"
        class="h-steps reveal-on-scroll"
      >
        <div class="h-container">
          <div class="h-section-header">
            <span class="h-eyebrow">{{ content.steps.eyebrow }}</span>
            <h2 class="h-section-title">
              {{ content.steps.title }}
            </h2>
          </div>
          <div class="h-steps-row">
            <div
              v-for="(s, i) in content.steps.items"
              :key="i"
              class="h-step-item"
            >
              <div class="h-step-num">
                {{ s.num }}
              </div>
              <h3>{{ s.title }}</h3>
              <p>{{ s.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Reviews -->
      <section
        id="reviews"
        class="h-reviews reveal-on-scroll"
      >
        <div class="h-container">
          <div class="h-section-header">
            <span class="h-eyebrow">{{ content.reviews.eyebrow }}</span>
            <h2 class="h-section-title">
              {{ content.reviews.title }}
            </h2>
          </div>
          <div class="h-reviews-grid">
            <div
              v-for="(r, i) in content.reviews.items"
              :key="i"
              class="h-review-card"
            >
              <div class="h-stars">
                ★★★★★
              </div>
              <p>"{{ r.text }}"</p>
              <div class="h-user">
                <span class="h-avatar">{{ r.avatar }}</span>
                <div>
                  <strong>{{ r.name }}</strong>
                  <span>{{ content.reviews.role }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LatestNewsSection
        :articles="latestNews"
        :all-news-url="allNewsUrl"
        :copy="content.news"
        :locale="locale"
      />
    </main>

    <!-- Footer -->
    <footer class="h-footer">
      <div class="h-container h-footer-grid">
        <div class="h-footer-brand">
          <div class="h-brand">
            <img
              :src="appIcon"
              :alt="content.images.logoAlt"
              class="h-logo"
            >
            <span class="h-title">{{ content.brand }}</span>
          </div>
          <p>{{ content.footer.tagline }}</p>
        </div>
        <div class="h-footer-nav">
          <div
            v-for="(col, i) in content.footer.columns"
            :key="i"
            class="h-footer-col"
          >
            <h4>{{ col.title }}</h4>
            <a
              v-for="(link, j) in col.links"
              :key="j"
              :href="link.url"
              class="h-footer-link"
            >
              <svg
                v-if="link.icon"
                class="h-footer-icon"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path :d="link.icon" />
              </svg>
              {{ link.text }}
            </a>
          </div>
        </div>
      </div>
      <div class="h-footer-bottom">
        <div class="h-container">
          <p>{{ content.footer.copyright }}</p>
        </div>
      </div>
    </footer>

    <!-- Lightbox Modal -->
    <Transition name="fade">
      <div
        v-if="showModal"
        class="h-modal"
        @click="closeLightbox"
      >
        <div
          class="h-modal-content"
          @click.stop
        >
          <img
            :src="selectedImage"
            class="h-modal-img"
            :alt="content.images.enlargedAlt"
          >
          <button
            class="h-modal-close"
            :aria-label="content.images.close"
            @click="closeLightbox"
          >
            &times;
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import appIcon from '../assets/app-icon.png'
import appleLogo from '../assets/apple-logo.png'
import googlePlayLogo from '../assets/gp-logo.png'
import screen1 from '../assets/screen/1.webp'
import screen2 from '../assets/screen/2.webp'
import screen3 from '../assets/screen/3.webp'
import screen4 from '../assets/screen/4.webp'
import screen5 from '../assets/screen/5.webp'
import screen6 from '../assets/screen/6.webp'
import screen7 from '../assets/screen/7.webp'
import screen8 from '../assets/screen/8.webp'
import { buildHomeContent, downloadLinks } from '../content/homeContent.mjs'
import { useLocale } from '../i18n/useLocale'
import { applyRouteSeo } from '../utils/seo'
import LatestNewsSection from '../components/LatestNewsSection.vue'
import { allNewsUrl, latestNews } from '../content/latestNews.mjs'

const isScrolled = ref(false)
const showModal = ref(false)
const selectedImage = ref('')
const route = useRoute()
const { locale, localeOptions, setLocale } = useLocale()
const content = computed(() => buildHomeContent(locale.value))

const screens = [screen1, screen2, screen3, screen4, screen5, screen6, screen7, screen8]

const changeLocale = (event) => setLocale(event.target.value)

watch(locale, (nextLocale) => {
  applyRouteSeo(route, nextLocale)
}, { immediate: true })

const handleScroll = () => { isScrolled.value = window.scrollY > 50 }

let observer

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  observer = new IntersectionObserver((entries) => {
    entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('is-revealed') })
  }, { threshold: 0.1 })
  document.querySelectorAll('.reveal-on-scroll').forEach(el => observer.observe(el))
})

const openLightbox = (img) => {
  selectedImage.value = img
  showModal.value = true
  document.body.style.overflow = 'hidden'
}

const closeLightbox = () => {
  showModal.value = false
  document.body.style.overflow = ''
}

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  observer?.disconnect()
  document.body.style.overflow = ''
})
</script>

<style scoped>
/* Reset & Base */
.h-app {
  --primary: #FF8BA7;
  --text-main: #1a1a1a;
  --text-sub: #666;
  --bg-main: #fffbf0;
  
  color: var(--text-main);
  background-color: var(--bg-main);
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  line-height: 1.5;
  overflow-x: hidden;
  scroll-behavior: smooth;
}

.h-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

/* Nav */
.h-nav {
  position: fixed; top: 0; left: 0; width: 100%; z-index: 1000;
  padding: 1.5rem 0; transition: all 0.3s ease;
  background: transparent;
}
.h-nav.is-shrunk {
  padding: 1rem 0; background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px); box-shadow: 0 4px 20px rgba(0,0,0,0.05);
}
.h-nav-inner { display: flex; align-items: center; justify-content: space-between; }
.h-brand { display: flex; align-items: center; gap: 0.75rem; }
.h-logo { width: 40px; height: 40px; border-radius: 10px; }
.h-title { font-size: 1.25rem; font-weight: 800; letter-spacing: -0.02em; }
.h-nav-links { display: flex; gap: 1.6rem; }
.h-nav-links a { font-weight: 600; text-decoration: none; color: var(--text-main); transition: color 0.2s; }
.h-nav-links a:hover { color: var(--primary); }
.h-nav-actions { display: flex; align-items: center; gap: 0.75rem; }
.h-language-picker select {
  max-width: 150px; padding: 0.58rem 2rem 0.58rem 0.8rem;
  color: var(--text-main); background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 99px;
  font: inherit; font-size: 0.85rem; font-weight: 700; cursor: pointer;
}
.sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
}
.h-cta-small { 
  background: var(--text-main); color: #fff; text-decoration: none;
  padding: 0.6rem 1.2rem; border-radius: 99px; font-weight: 700; font-size: 0.9rem;
}

/* Hero */
.h-hero {
  padding: 8rem 0 4rem;
  background: radial-gradient(circle at 100% 0%, #fff0f5 0%, transparent 40%);
}
.h-hero-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 4rem; align-items: center;
}
.h-hero-title { font-size: 4.5rem; line-height: 1.1; font-weight: 900; letter-spacing: -0.04em; margin-bottom: 2rem; }
.h-accent { color: var(--primary); }
.h-hero-subtitle { font-size: 1.25rem; color: var(--text-sub); margin-bottom: 3rem; max-width: 500px; }
.h-cta-group { display: flex; gap: 1rem; flex-wrap: wrap; }

.h-badge {
  display: flex; align-items: center; gap: 0.8rem; padding: 0.8rem 1.5rem;
  background: #000; color: #fff; text-decoration: none; border-radius: 16px;
  transition: transform 0.2s;
}
.h-badge.google { background: #fff; color: #000; border: 1px solid rgba(0,0,0,0.1); }
.h-badge:hover { transform: translateY(-4px); }
.h-badge img { height: 24px; }
.h-badge-text { display: flex; flex-direction: column; line-height: 1.1; }
.h-small { font-size: 0.65rem; opacity: 0.8; }
.h-large { font-size: 1rem; font-weight: 700; }

.h-hero-visual { position: relative; }
.h-screen-matrix {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem;
  transform: perspective(1000px) rotateY(-15deg) rotateX(10deg) scale(0.9);
  transition: transform 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
  transform-style: preserve-3d;
}
.h-screen-matrix:hover {
  transform: perspective(1000px) rotateY(0deg) rotateX(0deg) scale(0.95);
}
.h-screen-item {
  background: #000; border-radius: 12px; overflow: hidden; aspect-ratio: 9/19.5;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  animation: float 4s ease-in-out infinite;
  animation-delay: calc(var(--i) * 0.2s);
  transition: all 0.5s cubic-bezier(0.2, 0.8, 0.2, 1);
  cursor: pointer;
  position: relative;
  z-index: 1;
}
.h-screen-item:hover {
  transform: translateY(-20px) scale(1.1) translateZ(50px);
  z-index: 50;
  box-shadow: 0 30px 60px rgba(0,0,0,0.3);
  animation-play-state: paused;
}
.h-screen-item img { width: 100%; height: 100%; object-fit: cover; }
.h-hero-glow {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  width: 150%; height: 150%; background: radial-gradient(circle, rgba(255,139,167,0.15) 0%, transparent 70%);
  pointer-events: none; z-index: -1;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-15px); }
}

/* Sections Global */
.h-section-header { text-align: center; margin-bottom: 4rem; }
.h-eyebrow { 
  display: block; font-size: 0.85rem; font-weight: 800; color: var(--primary);
  text-transform: uppercase; letter-spacing: 0.2em; margin-bottom: 1rem;
}
.h-section-title { font-size: 3rem; font-weight: 800; letter-spacing: -0.03em; }
.h-section-header--compact { max-width: 820px; margin-left: auto; margin-right: auto; }
.h-section-copy { max-width: 720px; margin: 1rem auto 0; color: var(--text-sub); font-size: 1.08rem; }

.reveal-on-scroll { opacity: 0; transform: translateY(30px); transition: all 0.8s ease-out; }
.reveal-on-scroll.is-revealed { opacity: 1; transform: translateY(0); }
.h-tools, .h-assessments, .h-guidance, .h-features, .h-steps, .h-reviews { scroll-margin-top: 88px; }

/* Tools */
.h-tools { padding: 2rem 0 1rem; }
.h-tools-shell {
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(255, 255, 255, 0.88), rgba(255, 255, 255, 0.55)),
    linear-gradient(135deg, rgba(255, 193, 204, 0.32), rgba(163, 196, 243, 0.2) 52%, rgba(253, 228, 207, 0.42));
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 36px;
  padding: 2.5rem;
  box-shadow: 0 30px 80px rgba(255, 139, 167, 0.08);
}
.h-tools-shell::before {
  content: '';
  position: absolute;
  inset: auto -10% -45% 55%;
  height: 300px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.8), transparent 70%);
  pointer-events: none;
}
.h-tools-layout {
  display: grid;
  grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;
}
.h-tools-intro {
  max-width: 320px;
}
.h-tools-copy {
  margin: 0.85rem 0 0;
  font-size: 1rem;
  color: var(--text-sub);
}
.h-tools-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}
.h-tool-card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 190px;
  padding: 1.2rem;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.84);
  border: 1px solid rgba(207, 169, 181, 0.65);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    0 10px 24px rgba(120, 91, 111, 0.08),
    0 0 0 1px rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(14px);
  text-decoration: none;
  color: inherit;
  transition: transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease;
}
.h-tool-card::after {
  content: '';
  position: absolute;
  inset: auto -20% -25% auto;
  width: 130px;
  height: 130px;
  border-radius: 50%;
  background: var(--tool-glow);
  filter: blur(10px);
  opacity: 0.85;
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.h-tool-card:hover {
  transform: translateY(-8px);
  border-color: color-mix(in srgb, var(--tool-accent) 45%, white);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    0 24px 48px rgba(36, 24, 45, 0.14),
    0 0 0 1px rgba(255, 255, 255, 0.7);
}
.h-tool-card:hover::after {
  transform: scale(1.08);
  opacity: 1;
}
.h-tool-head {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-bottom: 0.9rem;
}
.h-tool-emoji {
  width: 2.8rem;
  height: 2.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.72);
  font-size: 1.4rem;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
}
.h-tool-heading {
  min-width: 0;
}
.h-tool-tag {
  display: inline-flex;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.88);
  color: var(--tool-accent);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.h-tool-card h3 {
  margin: 0.55rem 0 0;
  font-size: 1.12rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}
.h-tool-card p {
  margin: 0;
  color: var(--text-sub);
  font-size: 0.92rem;
  line-height: 1.45;
}
.h-tool-link {
  margin-top: auto;
  padding-top: 1rem;
  font-weight: 800;
  font-size: 0.92rem;
  color: var(--tool-accent);
}

/* Assessments */
.h-assessments { padding: 6rem 0; }
.h-assessment-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1.25rem; }
.h-assessment-card {
  display: flex; flex-direction: column; min-height: 300px; padding: 1.65rem;
  color: inherit; text-decoration: none; background: #fff; border-radius: 24px;
  border: 1px solid color-mix(in srgb, var(--assessment-color) 22%, white);
  box-shadow: 0 12px 36px rgba(43, 35, 52, 0.06); transition: transform 0.28s ease, box-shadow 0.28s ease;
}
.h-assessment-card:hover { transform: translateY(-8px); box-shadow: 0 24px 48px rgba(43, 35, 52, 0.12); }
.h-assessment-topline { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
.h-assessment-icon { display: grid; width: 3rem; height: 3rem; place-items: center; border-radius: 15px; background: color-mix(in srgb, var(--assessment-color) 12%, white); font-size: 1.45rem; }
.h-assessment-meta { color: var(--text-sub); font-size: 0.78rem; font-weight: 700; }
.h-assessment-eyebrow { margin-top: 1.35rem; color: var(--assessment-color); font-size: 0.72rem; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; }
.h-assessment-card h3 { margin: 0.55rem 0 0.7rem; font-size: 1.32rem; line-height: 1.2; }
.h-assessment-card p { margin: 0; color: var(--text-sub); font-size: 0.93rem; }
.h-assessment-link { margin-top: auto; padding-top: 1.5rem; color: var(--assessment-color); font-weight: 800; }
.h-assessment-disclaimer { max-width: 760px; margin: 1.5rem auto 0; color: var(--text-sub); font-size: 0.82rem; text-align: center; }

/* Mystical guidance */
.h-guidance { padding: 6rem 0; background: linear-gradient(145deg, #242039, #342548 54%, #4a2f54); color: #fff; }
.h-guidance-layout { display: grid; grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr); gap: 4rem; align-items: center; }
.h-guidance-intro .h-eyebrow { color: #ffb2c5; }
.h-guidance-intro p { max-width: 520px; color: rgba(255, 255, 255, 0.72); font-size: 1.08rem; }
.h-guidance-cards { display: grid; gap: 1rem; }
.h-guidance-card { display: flex; gap: 1.15rem; padding: 1.3rem; color: #fff; text-decoration: none; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.13); border-radius: 22px; backdrop-filter: blur(12px); transition: transform 0.25s ease, background 0.25s ease; }
.h-guidance-card:hover { transform: translateX(8px); background: rgba(255, 255, 255, 0.13); }
.h-guidance-icon { display: grid; flex: 0 0 auto; width: 3.2rem; height: 3.2rem; place-items: center; border-radius: 16px; background: rgba(255, 255, 255, 0.12); font-size: 1.5rem; }
.h-guidance-card h3 { margin: 0 0 0.4rem; font-size: 1.15rem; }
.h-guidance-card p { margin: 0; color: rgba(255, 255, 255, 0.7); font-size: 0.9rem; }
.h-guidance-card small { display: block; margin-top: 0.7rem; color: #ffc4d2; font-weight: 700; }

/* Features */
.h-features { padding: 6rem 0; }
.h-features-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 2rem; }
.h-feature-card {
  background: #fff; padding: 2.5rem 2rem; border-radius: 24px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.03); border: 1px solid rgba(0,0,0,0.03);
  transition: transform 0.3s ease;
}
.h-feature-card:hover { transform: translateY(-10px); }
.h-feature-icon {
  width: 60px; height: 60px; background: #fff0f5; border-radius: 16px;
  display: flex; align-items: center; justify-content: center; font-size: 2rem;
  margin-bottom: 1.5rem; color: var(--bg);
}
.h-feature-card h3 { font-size: 1.25rem; font-weight: 700; margin-bottom: 0.75rem; }
.h-feature-card p { color: var(--text-sub); font-size: 0.95rem; }

/* Steps */
.h-steps { padding: 6rem 0; background: #fff; }
.h-steps-row { display: flex; justify-content: space-between; gap: 3rem; margin-top: 2rem; }
.h-step-item { flex: 1; position: relative; }
.h-step-num { font-size: 3.5rem; font-weight: 900; color: var(--primary); opacity: 0.2; margin-bottom: -1.5rem; }
.h-step-item h3 { font-size: 1.5rem; font-weight: 800; margin-bottom: 1rem; }
.h-step-item p { color: var(--text-sub); }

/* Reviews */
.h-reviews { padding: 6rem 0; }
.h-reviews-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; }
.h-review-card { background: #fff; padding: 2.5rem; border-radius: 24px; box-shadow: 0 4px 20px rgba(0,0,0,0.03); }
.h-stars { color: #FFB800; font-size: 0.8rem; margin-bottom: 1rem; }
.h-review-card p { font-size: 1.1rem; font-weight: 500; font-style: italic; margin-bottom: 2rem; }
.h-user { display: flex; align-items: center; gap: 1rem; }
.h-avatar { width: 44px; height: 44px; border-radius: 50%; background: #f0f0f0; display: flex; align-items: center; justify-content: center; font-size: 1.25rem; }
.h-user strong { display: block; font-size: 0.95rem; }
.h-user span { font-size: 0.8rem; color: var(--text-sub); }

/* Footer */
.h-footer { padding: 8rem 0 0; background: #fff; border-top: 1px solid rgba(0,0,0,0.05); }
.h-footer-grid { display: grid; grid-template-columns: 1.5fr 3fr; gap: 4rem; margin-bottom: 6rem; }
.h-footer-brand p { margin-top: 1.5rem; color: var(--text-sub); max-width: 260px; }
.h-footer-nav { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; }
.h-footer-col h4 { font-weight: 800; margin-bottom: 1.5rem; }
.h-footer-col a { display: block; text-decoration: none; color: var(--text-sub); margin-bottom: 0.8rem; transition: color 0.2s; }
.h-footer-link { display: flex !important; align-items: center; gap: 0.5rem; }
.h-footer-icon { width: 1.1rem; height: 1.1rem; }
.h-footer-col a:hover { color: var(--primary); }
.h-footer-bottom { padding: 2rem 0; border-top: 1px solid rgba(0,0,0,0.05); text-align: center; font-size: 0.9rem; color: var(--text-sub); }

/* Responsive */
@media (max-width: 1024px) {
  .h-hero-grid { grid-template-columns: 1fr; text-align: center; gap: 4rem; }
  .h-hero-text { display: flex; flex-direction: column; align-items: center; }
  .h-hero-title { font-size: 3.5rem; }
  .h-hero-subtitle { margin-left: auto; margin-right: auto; }
  .h-screen-matrix { transform: none; gap: 0.75rem; }
  .h-tools-layout { grid-template-columns: 1fr; }
  .h-tools-intro { max-width: 620px; margin: 0 auto; text-align: center; }
  .h-tools-grid { grid-template-columns: repeat(2, 1fr); }
  .h-assessment-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .h-guidance-layout { grid-template-columns: 1fr; gap: 2.5rem; }
  .h-features-grid { grid-template-columns: repeat(2, 1fr); }
  .h-reviews-grid { grid-template-columns: 1fr; }
  .h-footer-grid { grid-template-columns: 1fr; text-align: center; }
  .h-footer-brand { display: flex; flex-direction: column; align-items: center; }
  .h-footer-nav { justify-items: center; }
}

@media (max-width: 920px) {
  .desktop-only { display: none; }
}

@media (max-width: 768px) {
  .h-nav { background: #fff; padding: 1rem 0; }
  .h-title { display: none; }
  .h-language-picker select { max-width: 120px; }
  .h-cta-small { padding: 0.6rem 0.9rem; }
  .desktop-only { display: none; }
  .h-hero { padding-top: 7rem; }
  .h-hero-title { font-size: 2.75rem; }
  .h-screen-matrix { grid-template-columns: repeat(2, 1fr); padding: 0 1rem; }
  .h-section-title { font-size: 2.25rem; }
  .h-tools-shell { padding: 1.75rem 1.2rem; border-radius: 28px; }
  .h-tools-intro { text-align: center; margin-left: auto; margin-right: auto; }
  .h-tools-grid { grid-template-columns: 1fr; }
  .h-tool-card { min-height: auto; }
  .h-assessment-grid { grid-template-columns: 1fr; }
  .h-features-grid { grid-template-columns: 1fr; }
  .h-steps-row { flex-direction: column; text-align: center; gap: 4rem; }
  .h-footer-nav { grid-template-columns: 1fr; gap: 3rem; }
}

/* Modal */
.h-modal {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.8); backdrop-filter: blur(10px);
  z-index: 2000; display: flex; align-items: center; justify-content: center;
  padding: 2rem; cursor: pointer;
}
.h-modal-content {
  position: relative; max-width: 90vw; max-height: 90vh;
}
.h-modal-img {
  max-width: 100%; max-height: 90vh; border-radius: 20px;
  box-shadow: 0 30px 60px rgba(0,0,0,0.5);
}
.h-modal-close {
  position: absolute; top: -1.5rem; right: -1.5rem;
  background: #fff; border: none; width: 40px; height: 40px;
  border-radius: 50%; font-size: 1.5rem; font-weight: bold;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 10px rgba(0,0,0,0.2);
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
